import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import CategoryBadge from '@/components/base/CategoryBadge';
import TopicCard from '@/components/feature/TopicCard';
import useSeo from '@/hooks/useSeo';
import { topics } from '@/mocks/topics';

export default function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const topic = topics.find((t) => t.slug === slug);

  const related = topic
    ? topics
        .filter((t) => t.slug !== topic.slug)
        .sort((a, b) => {
          const aSame = a.category === topic.category ? 1 : 0;
          const bSame = b.category === topic.category ? 1 : 0;
          if (aSame !== bSame) return bSame - aSame;
          return b.heat - a.heat;
        })
        .slice(0, 3)
    : [];

  useSeo({
    title: topic ? `${topic.title}｜锐察社议题追踪` : '议题未找到｜锐察社',
    description: topic
      ? `${topic.summary.slice(0, 110)}`
      : '该议题可能已被移除或链接有误，返回议题追踪查看其他内容。',
    keywords: topic ? `${topic.tags.join(',')},社会爆料,议题盘点` : '社会爆料,议题盘点',
    path: topic ? `/topics/${topic.slug}` : '/topics',
    type: 'article',
    jsonLd: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: topic.title,
            description: topic.summary,
            datePublished: topic.date,
            articleSection: topic.category,
            keywords: topic.tags.join(','),
            inLanguage: 'zh-CN',
            author: { '@type': 'Organization', name: '锐察社' },
            publisher: { '@type': 'Organization', name: '锐察社' },
            mainEntityOfPage: `https://sjbxw.cn/topics/${topic.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sjbxw.cn/' },
              { '@type': 'ListItem', position: 2, name: '议题追踪', item: 'https://sjbxw.cn/topics' },
              {
                '@type': 'ListItem',
                position: 3,
                name: topic.title,
                item: `https://sjbxw.cn/topics/${topic.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-[1200px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            没有找到这个议题
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            链接可能已失效，或者该议题尚未收录。你可以返回议题追踪，看看其他正在跟进的内容。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-700"
          >
            返回议题追踪
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-300 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-4 md:px-6">
          <nav aria-label="面包屑" className="font-label text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="transition-colors hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/topics" className="transition-colors hover:text-primary-700">议题追踪</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-foreground-800">{topic.category}</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <header className="animate-rise">
              <div className="flex flex-wrap items-center gap-3">
                <CategoryBadge category={topic.category} />
                <span className="font-label text-xs text-foreground-500">{topic.date}</span>
                <span className="font-label text-xs text-foreground-500">
                  约 {topic.readTime} 分钟阅读
                </span>
                <span className="inline-flex items-center gap-1 font-label text-xs text-accent-700">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  {topic.heat.toLocaleString('zh-CN')}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                {topic.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{topic.summary}</p>
            </header>

            <figure className="mt-7 overflow-hidden rounded-lg bg-background-200">
              <div className="h-[240px] w-full md:h-[400px]">
                <img
                  src={topic.cover}
                  alt={`${topic.title} - 社会爆料议题配图`}
                  title={`${topic.title} - 社会爆料`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="px-4 py-3 font-label text-xs text-foreground-500">
                配图仅用于议题氛围示意，不代表具体当事方
              </figcaption>
            </figure>

            <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-lightbulb-flash-line text-primary-600" aria-hidden="true"></i>
                快速要点
              </h2>
              <ul className="mt-4 space-y-3">
                {topic.keyPoints.map((point) => (
                  <li key={point} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 space-y-10">
              {topic.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 18)} className="text-base leading-loose text-foreground-700">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-timeline-view text-primary-600" aria-hidden="true"></i>
                事件脉络
              </h2>
              <ol className="mt-6 border-l-2 border-background-300 pl-6">
                {topic.timeline.map((item) => (
                  <li key={item.time} className="relative pb-7 last:pb-0">
                    <span className="absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full border-2 border-background-50 bg-accent-500"></span>
                    <p className="font-label text-sm font-medium text-accent-800">{item.time}</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.event}</p>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-300 pt-6">
              <span className="font-label text-sm text-foreground-500">相关标签：</span>
              {topic.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-lg border border-background-300 bg-background-100 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">议题信息</h3>
                <dl className="mt-4 space-y-3 font-label text-sm">
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">所属分类</dt>
                    <dd className="text-foreground-900">{topic.category}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">更新时间</dt>
                    <dd className="text-foreground-900">{topic.date}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">讨论热度</dt>
                    <dd className="text-accent-700">{topic.heat.toLocaleString('zh-CN')}</dd>
                  </div>
                </dl>
                <Link
                  to="/guide"
                  className="mt-5 flex items-center justify-center gap-1.5 rounded-md bg-foreground-900 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-foreground-950"
                >
                  查看维权指南
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">有相关线索？</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  如果你掌握该议题的补充信息或后续进展，欢迎联系我们，帮助把脉络补完整。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex items-center gap-1.5 font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
                >
                  提供线索
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14 border-t border-background-300 pt-10">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">相关议题推荐</h2>
            <Link
              to="/topics"
              className="font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
            >
              更多议题
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <TopicCard key={item.slug} topic={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}